package top.wcpe.allincore.ui.web

import top.wcpe.allincore.ui.runtime.render.UiRenderBridge
import top.wcpe.allincore.ui.runtime.render.layer.DrawTraceEntry
import top.wcpe.allincore.ui.runtime.render.layer.DrawTraceObserver
import top.wcpe.allincore.ui.runtime.render.layer.DrawTracePhase
import top.wcpe.allincore.ui.runtime.render.layer.StateSnapshot
import top.wcpe.allincore.ui.runtime.render.layer.TransformEntry
import top.wcpe.allincore.ui.runtime.render.layer.UnsupportedRenderCapability
import top.wcpe.allincore.ui.runtime.render.layer.UnsupportedRenderContext
import top.wcpe.allincore.ui.runtime.render.model.GradientType
import top.wcpe.allincore.ui.runtime.render.model.ImageStyle
import top.wcpe.allincore.ui.runtime.render.model.MaskType
import top.wcpe.allincore.ui.runtime.render.model.RenderBatch
import top.wcpe.allincore.ui.runtime.render.model.RenderCommand
import top.wcpe.allincore.ui.runtime.render.model.RenderCommandType
import top.wcpe.allincore.ui.runtime.render.model.ShadowParams
import top.wcpe.allincore.ui.runtime.render.model.StyledFill
import kotlin.js.json
import kotlin.math.cos
import kotlin.math.min
import kotlin.math.sin

/** PixiJS 的中立渲染桥实现，只消费 LayerBufferingBridge 已确定的实际回放顺序。 */
@Suppress("TooManyFunctions", "LongParameterList", "MagicNumber")
internal class PixiRenderBridge(
    private val stage: Container,
    screenWidth: Float,
    screenHeight: Float,
    private val sessionId: String = "web-preview",
    private val generation: () -> Int = { 0 },
    private val diagnostics: WebRenderDiagnosticSink = defaultWebRenderDiagnosticSink(),
    private val textureResolver: (String) -> Texture? = { resource ->
        Assets.get(resource).unsafeCast<Texture?>()
    },
    private val resources: WebRenderResourceRegistry = WebRenderResourceRegistry(),
    private val traceConsumer: (DrawTraceEntry) -> Unit = {},
    modelCompositionHost: ModelCompositionHost? = null,
    private val requestPresent: () -> Unit = {},
) : UiRenderBridge,
    DrawTraceObserver {

    private val containerStack = mutableListOf<Container>()
    private val zStack = mutableListOf<Float>()
    private val frameRoots = mutableListOf<Container>()
    private val frameLeases = mutableListOf<WebRenderResourceLease>()
    private var current: Container = stage
    private var screenW = screenWidth
    private var screenH = screenHeight
    private var currentTrace: DrawTraceEntry? = null
    private var currentZ = 0f
    private var frameGeneration = 0
    private var disposed = false
    private var compositionHostDisposed = false
    private val ownsCompositionHost = modelCompositionHost == null
    private val compositionHost = modelCompositionHost ?: PlaceholderModelCompositionHost(
        sessionId,
        generation,
        { disposed },
        diagnostics,
    )

    fun beginFrame() {
        frameGeneration++
        destroyFrameObjects()
        current = stage
        currentTrace = null
        containerStack.clear()
        zStack.clear()
        currentZ = 0f
        stage.sortableChildren = true
    }

    fun endFrame() {
        currentTrace = null
    }

    fun resize(width: Float, height: Float) {
        screenW = width
        screenH = height
    }

    fun dispose(): Boolean {
        disposed = true
        disposeOwnedCompositionHost()
        val released = destroyFrameObjects()
        if (released) {
            containerStack.clear()
            zStack.clear()
            current = stage
            currentTrace = null
            currentZ = 0f
        }
        return released
    }

    fun resourceCounts(): WebRenderResourceCounts = resources.snapshot()

    override fun onDrawTrace(entry: DrawTraceEntry) {
        currentTrace = entry
        traceConsumer(entry)
    }

    override fun onUnsupportedCapability(capability: UnsupportedRenderCapability, entry: DrawTraceEntry) {
        emitUnsupported(capabilityKey(capability), entry)
    }

    override fun onUnsupportedCapability(capability: UnsupportedRenderCapability, context: UnsupportedRenderContext) {
        emitUnsupported(capabilityKey(capability), context.phase, context.sequence)
    }

    override fun fillRect(x: Float, y: Float, width: Float, height: Float, argb: Int) {
        if (width <= 0f || height <= 0f || alpha(argb) <= 0.0) return
        add(
            Graphics()
                .rect(x.toDouble(), y.toDouble(), width.toDouble(), height.toDouble())
                .fill(json("color" to rgb(argb), "alpha" to alpha(argb))),
        )
    }

    override fun drawText(text: String, x: Float, y: Float, argb: Int, scale: Float) {
        drawText(text, x, y, argb, scale, null, false, false, false)
    }

    override fun drawText(
        text: String,
        x: Float,
        y: Float,
        argb: Int,
        scale: Float,
        fontName: String?,
        shadow: Boolean,
        bold: Boolean,
        italic: Boolean,
    ) {
        if (shadow) {
            addText(text, x + 1f, y + 1f, shadowColor(argb), scale, fontName, bold, italic)
        }
        addText(text, x, y, argb, scale, fontName, bold, italic)
    }

    override fun supportsRichText(): Boolean = true

    override fun drawRichText(
        text: String,
        x: Float,
        y: Float,
        argb: Int,
        scale: Float,
        fontName: String?,
        shadow: Boolean,
        bold: Boolean,
        italic: Boolean,
        maxWidth: Float,
        multiline: Boolean,
        lineStep: Float,
    ) {
        val actualLineStep = if (lineStep > 0f) lineStep else TEXT_HEIGHT * scale
        val layout = layoutRichText(text, scale, fontName, bold, maxWidth, multiline, actualLineStep)
        layout.lines.forEachIndexed { index, line ->
            drawRichLine(line, x, y + index * layout.lineStep, argb, scale, fontName, shadow, bold, italic)
        }
        if (layout.hasInlineResource) {
            emitPlaceholder(WebRenderCapabilityKeys.DRAW_RICH_TEXT, "", "富文本内联资源使用规范占位")
        }
    }

    override fun measureRichText(
        text: String,
        scale: Float,
        fontName: String?,
        bold: Boolean,
        maxWidth: Float,
        multiline: Boolean,
    ): FloatArray {
        val layout = layoutRichText(text, scale, fontName, bold, maxWidth, multiline, TEXT_HEIGHT * scale)
        return floatArrayOf(layout.width, layout.lines.size * layout.lineStep)
    }

    override fun drawLine(x1: Float, y1: Float, x2: Float, y2: Float, thickness: Float, argb: Int) {
        add(
            Graphics()
                .moveTo(x1.toDouble(), y1.toDouble())
                .lineTo(x2.toDouble(), y2.toDouble())
                .stroke(json("width" to thickness.toDouble(), "color" to rgb(argb), "alpha" to alpha(argb))),
        )
    }

    override fun pushClip(x: Float, y: Float, width: Float, height: Float) {
        val clipped = Container()
        val mask = Graphics()
            .rect(x.toDouble(), y.toDouble(), width.toDouble(), height.toDouble())
            .fill(json("color" to 0xFFFFFF))
        clipped.addChild(mask)
        clipped.mask = mask
        registerDisplayObject()
        frameLeases.add(resources.register(WebRenderResourceKind.MASK))
        pushContainer(clipped)
    }

    override fun popClip() = popContainer()

    override fun supportsRuntimeTransform(): Boolean = true

    override fun pushTransform(tx: Float, ty: Float, scaleX: Float, scaleY: Float) {
        val transformed = Container()
        transformed.x = tx.toDouble()
        transformed.y = ty.toDouble()
        transformed.scale.set(scaleX, scaleY)
        pushContainer(transformed)
    }

    override fun popTransform() = popContainer()

    override fun supportsRuntimeAlpha(): Boolean = true

    override fun pushAlpha(alpha: Float) {
        val transparent = Container()
        transparent.alpha = alpha.coerceIn(0f, 1f).toDouble()
        pushContainer(transparent)
    }

    override fun popAlpha() = popContainer()

    override fun supportsColorMatrix(): Boolean = false
    override fun pushColorMatrix(matrix4x5: FloatArray) = emitUnsupported(COLOR_MATRIX_KEY)
    override fun popColorMatrix() = emitUnsupported(POP_COLOR_MATRIX_KEY)
    override fun supportsFullTransform(): Boolean = false
    override fun pushTransform3x3(mat3x3: FloatArray) = emitUnsupported(TRANSFORM_3_KEY)
    override fun popTransform3x3() = emitUnsupported(POP_TRANSFORM_3_KEY)
    override fun pushTransform4x4(mat4x4: FloatArray) = emitUnsupported(TRANSFORM_4_KEY)
    override fun popTransform4x4() = emitUnsupported(POP_TRANSFORM_4_KEY)
    override fun supportsFilterGroup(): Boolean = false
    override fun beginFilterGroup(blurRadius: Float, x: Float, y: Float, w: Float, h: Float) = emitUnsupported(FILTER_KEY)
    override fun endFilterGroup() = emitUnsupported(END_FILTER_KEY)
    override fun supportsBackdropFilter(): Boolean = false
    override fun applyBackdropBlur(blurRadius: Float, x: Float, y: Float, w: Float, h: Float) = emitUnsupported(BACKDROP_KEY)
    override fun supportsBlendMode(): Boolean = false
    override fun pushBlendMode(mode: Int) = emitUnsupported(BLEND_KEY)
    override fun popBlendMode() = emitUnsupported(POP_BLEND_KEY)

    override fun pushZ(z: Float) {
        zStack.add(currentZ)
        currentZ += z
        current.sortableChildren = true
    }

    override fun popZ() {
        if (zStack.isNotEmpty()) {
            currentZ = zStack.removeAt(zStack.lastIndex)
        }
    }

    override fun flush() = Unit

    override fun textWidth(text: String, scale: Float): Float = textWidth(text, scale, null, false)

    override fun textWidth(text: String, scale: Float, fontName: String?, bold: Boolean): Float {
        val style = textStyle(0xFFFFFFFF.toInt(), scale, fontName, bold, false)
        return (CanvasTextMetrics.measureText(text, style).width as Double).toFloat()
    }

    override fun screenWidth(): Float = screenW
    override fun screenHeight(): Float = screenH
    override fun textureExists(resource: String): Boolean =
        resource.isNotBlank() && runCatching { textureResolver(resource) != null }.getOrDefault(false)
    override fun screenSpaceBridge(): UiRenderBridge = this
    override fun supportsBatching(): Boolean = true

    override fun drawImage(resource: String, x: Float, y: Float, width: Float, height: Float) {
        if (resource.isBlank()) {
            drawMissingResource(resource, x, y, width, height)
            return
        }
        val texture = runCatching { textureResolver(resource) }.getOrNull()
        if (texture == null) {
            drawMissingResource(resource, x, y, width, height)
            return
        }
        addSprite(Sprite(texture), x, y, width, height, ownedTexture = null, borrowedTexture = true)
    }

    override fun drawImageStyled(resource: String, x: Float, y: Float, w: Float, h: Float, style: ImageStyle?) {
        drawImage(resource, x, y, w, h)
        emitUnsupported(IMAGE_STYLE_KEY)
    }

    override fun drawTexturedQuad(
        resource: String,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        uvX: Float,
        uvY: Float,
        uvW: Float,
        uvH: Float,
    ) {
        if (resource.isBlank() || uvW <= 0f || uvH <= 0f) {
            drawMissingResource(resource, x, y, width, height)
            return
        }
        val texture = runCatching {
            val source = textureResolver(resource)?.source ?: error("贴图尚未加载")
            Texture(
                json(
                    "source" to source,
                    "frame" to Rectangle(uvX.toDouble(), uvY.toDouble(), uvW.toDouble(), uvH.toDouble()),
                ),
            )
        }.getOrElse {
            drawMissingResource(resource, x, y, width, height)
            return
        }
        addSprite(Sprite(json("texture" to texture)), x, y, width, height, ownedTexture = texture, borrowedTexture = false)
    }

    override fun drawItemStack(itemId: String, count: Int, x: Float, y: Float, width: Float, height: Float) {
        drawItemPlaceholder(itemId, count, true, 0f, 0f, 0, x, y, width, height, ITEM_KEY_6)
    }

    override fun drawItemStack(
        itemId: String,
        count: Int,
        enchanted: Boolean,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
    ) {
        drawItemPlaceholder(itemId, count, true, 0f, 0f, 0, x, y, width, height, ITEM_KEY_7)
    }

    override fun drawItemStack(
        itemId: String,
        count: Int,
        enchanted: Boolean,
        showCount: Boolean,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
    ) {
        drawItemPlaceholder(itemId, count, showCount, 0f, 0f, 0, x, y, width, height, ITEM_KEY_8)
    }

    override fun drawItemStack(
        itemId: String,
        count: Int,
        enchanted: Boolean,
        showCount: Boolean,
        countAnchor: String?,
        countOffsetX: Float,
        countOffsetY: Float,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
    ) {
        drawItemPlaceholder(
            itemId, count, showCount, countOffsetX, countOffsetY, 0, x, y, width, height, ITEM_KEY_11,
        )
    }

    override fun drawItemStack(
        itemId: String,
        itemNbt: String?,
        count: Int,
        enchanted: Boolean,
        showCount: Boolean,
        countAnchor: String?,
        countOffsetX: Float,
        countOffsetY: Float,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
    ) {
        drawItemPlaceholder(
            itemId, count, showCount, countOffsetX, countOffsetY, 0, x, y, width, height, ITEM_KEY_12,
        )
    }

    override fun drawItemStack(
        itemId: String,
        itemNbt: String?,
        count: Int,
        enchanted: Boolean,
        showCount: Boolean,
        countAnchor: String?,
        countOffsetX: Float,
        countOffsetY: Float,
        backdropColor: Int,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
    ) {
        drawItemPlaceholder(
            itemId, count, showCount, countOffsetX, countOffsetY, backdropColor,
            x, y, width, height, ITEM_KEY_13,
        )
    }

    override fun drawEntityPreview(
        entityRef: String,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        mouseX: Float,
        mouseY: Float,
    ) {
        drawEntityPlaceholder(entityRef, null, null, x, y, width, height, ENTITY_KEY_7)
    }

    override fun drawEntityPreview(
        entityRef: String,
        entityType: String?,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        mouseX: Float,
        mouseY: Float,
        scale: Float,
        rotationX: Float,
        rotationY: Float,
        followMouse: Boolean,
    ) {
        drawEntityPlaceholder(entityRef, entityType, null, x, y, width, height, ENTITY_KEY_12)
    }

    override fun drawEntityPreview(
        entityRef: String,
        entityType: String?,
        entityName: String?,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        mouseX: Float,
        mouseY: Float,
        scale: Float,
        rotationX: Float,
        rotationY: Float,
        followMouse: Boolean,
    ) {
        drawEntityPlaceholder(entityRef, entityType, entityName, x, y, width, height, ENTITY_KEY_13)
    }

    override fun drawModelPreview(
        source: String,
        modelBase: String,
        texture: String?,
        instanceKey: String,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        scale: Float,
        rotationX: Float,
        rotationY: Float,
        interactive: Boolean,
        backgroundColor: Int,
    ) {
        val trace = currentTrace
        val record = trace?.record?.takeIf { it.type() == RenderCommandType.DRAW_MODEL_PREVIEW }
        val request = modelCompositionRequest(
            trace,
            record?.state(),
            record?.let { ModelCompositionRect(it.x(), it.y(), it.w(), it.h()) }
                ?: ModelCompositionRect(x, y, width, height),
            record?.modelInstanceKey() ?: instanceKey,
            record?.modelSource() ?: source,
            if (record == null) texture else record.modelTexture(),
            record?.modelBase() ?: modelBase,
            record?.modelScale() ?: scale,
            record?.modelRotationX() ?: rotationX,
            record?.modelRotationY() ?: rotationY,
            record?.modelInteractive() ?: interactive,
            record?.modelBackgroundColor() ?: backgroundColor,
        )
        if (!shouldComposeModel(request)) return
        val slot = createModelSlot(request.rect, request.backgroundColor)
        composeModel(request, slot, frameGeneration)
    }

    override fun applyNeonGlow(
        x: Float, y: Float, width: Float, height: Float, neonColor: Int,
        intensity: Float, size: Float, animated: Boolean, cornerRadii: FloatArray?,
    ) = emitUnsupported(NEON_GLOW_KEY)

    override fun applyRippleEffect(
        x: Float, y: Float, width: Float, height: Float, color: Int,
        progress: Float, radius: Float, centerX: Float, centerY: Float,
    ) = emitUnsupported(RIPPLE_KEY_9)

    override fun applyRippleEffect(
        x: Float, y: Float, width: Float, height: Float, color: Int,
        progress: Float, radius: Float, centerX: Float, centerY: Float, cornerRadii: FloatArray?,
    ) = emitUnsupported(RIPPLE_KEY_10)

    override fun applyProceduralTexture(
        x: Float, y: Float, width: Float, height: Float, kind: String?, colorA: Int,
        colorB: Int, scale: Float, density: Float, intensity: Float, cornerRadii: FloatArray?,
    ) = emitUnsupported(PROCEDURAL_TEXTURE_KEY)

    override fun applyFlow(
        x: Float, y: Float, width: Float, height: Float, flowColor: Int,
        speedHz: Float, widthPx: Float, angleDeg: Float, mode: Int, cornerRadii: FloatArray?,
    ) = emitUnsupported(FLOW_KEY)

    override fun applyEnergy(
        x: Float, y: Float, width: Float, height: Float, energyColor: Int,
        intensity: Float, frequency: Float, speed: Float, pattern: Int, cornerRadii: FloatArray?,
    ) = emitUnsupported(ENERGY_KEY)

    override fun submitBatch(batch: RenderBatch) {
        batch.commands.forEach(::submitCommand)
    }

    private fun submitCommand(command: RenderCommand) {
        when (command.type) {
            RenderCommandType.DRAW_RICH_TEXT -> submitRichText(command)
            RenderCommandType.DRAW_ITEM_STACK -> submitItem(command)
            RenderCommandType.DRAW_ENTITY_PREVIEW -> submitEntity(command)
            RenderCommandType.DRAW_MODEL_PREVIEW -> submitModel(command)
            RenderCommandType.IMAGE_STYLED -> drawImageStyled(
                command.texture.orEmpty(),
                command.x,
                command.y,
                command.w,
                command.h,
                command.imageStyle,
            )
            RenderCommandType.NEON_GLOW,
            RenderCommandType.RIPPLE,
            RenderCommandType.PROCEDURAL_TEXTURE,
            RenderCommandType.FLOW,
            RenderCommandType.ENERGY,
            RenderCommandType.FILTER_GROUP,
            RenderCommandType.BACKDROP_FILTER,
            RenderCommandType.BLEND_GROUP,
            RenderCommandType.MATERIAL_LAYER,
            RenderCommandType.HISTORY_GROUP,
            RenderCommandType.PARTICLE,
            -> emitUnsupported(WebRenderCapabilityMatrix.commandKey(command.type))
            RenderCommandType.FILL_RECT -> fillRect(command.x, command.y, command.w, command.h, command.argb)
            RenderCommandType.DRAW_TEXT -> drawText(
                command.text.orEmpty(),
                command.x,
                command.y,
                command.argb,
                command.scale,
            )
            RenderCommandType.DRAW_LINE -> drawLine(command.x, command.y, command.w, command.h, 1f, command.argb)
            RenderCommandType.DRAW_IMAGE -> drawImage(
                command.texture.orEmpty(),
                command.x,
                command.y,
                command.w,
                command.h,
            )
            RenderCommandType.DRAW_TEXTURED_QUAD -> drawTexturedQuad(
                command.texture.orEmpty(), command.x, command.y, command.w, command.h,
                command.uvX, command.uvY, command.uvW, command.uvH,
            )
            RenderCommandType.ROUNDED_RECT -> submitRoundedRect(command)
            RenderCommandType.GRADIENT -> submitGradient(command)
            RenderCommandType.SHADOW -> submitShadow(command)
        }
    }

    private fun submitRoundedRect(command: RenderCommand) {
        val fill = command.styledFill
        val graphics = styledShape(command, fill)
        if (alpha(command.argb) > 0.0) {
            graphics.fill(json("color" to rgb(command.argb), "alpha" to alpha(command.argb)))
        }
        strokeBorder(graphics, fill)
        addStyledGraphics(graphics, command, fill)
    }

    private fun submitGradient(command: RenderCommand) {
        val fill = command.styledFill
        if (fill == null || !fill.hasGradient()) {
            submitRoundedRect(command)
            return
        }
        val gradient = createGradient(fill)
        val gradientLease = resources.register(WebRenderResourceKind.OWNED_TEXTURE) { gradient.destroy() }
        frameLeases.add(gradientLease)
        frameLeases.add(resources.register(WebRenderResourceKind.OWNED_BASE_TEXTURE))
        val graphics = styledShape(command, fill).fill(gradient)
        strokeBorder(graphics, fill)
        addStyledGraphics(graphics, command, fill)
    }

    private fun submitShadow(command: RenderCommand) {
        val shadow = command.shadow ?: return
        if (alpha(shadow.color) <= 0.0) return
        if (shadow.inset) {
            submitInsetShadow(command, shadow)
        } else {
            submitOuterShadow(command, shadow)
        }
    }

    private fun submitOuterShadow(command: RenderCommand, shadow: ShadowParams) {
        val spread = shadow.spread
        val width = command.w + spread * 2f
        val height = command.h + spread * 2f
        if (width <= 0f || height <= 0f) return
        val shadowCommand = RenderCommand.roundedRect(
            command.x + shadow.offsetX - spread,
            command.y + shadow.offsetY - spread,
            width,
            height,
            shadow.color,
            command.clipId,
            command.styledFill,
        )
        val graphics = styledShape(shadowCommand, command.styledFill)
            .fill(json("color" to rgb(shadow.color), "alpha" to alpha(shadow.color)))
        applyBlur(graphics, shadow.blur)
        addStyledGraphics(graphics, shadowCommand, command.styledFill)
    }

    private fun submitInsetShadow(command: RenderCommand, shadow: ShadowParams) {
        val container = Container()
        val mask = styledShape(command, command.styledFill).fill(json("color" to 0xFFFFFF))
        container.addChild(mask)
        container.mask = mask
        registerDisplayObject()
        frameLeases.add(resources.register(WebRenderResourceKind.MASK))
        pushContainer(container)
        val depth = (shadow.blur + shadow.spread).coerceAtLeast(1f)
        val border = styledShape(command, command.styledFill)
            .stroke(
                json(
                    "width" to depth * 2.0,
                    "color" to rgb(shadow.color),
                    "alpha" to alpha(shadow.color),
                ),
            )
        border.x = shadow.offsetX.toDouble()
        border.y = shadow.offsetY.toDouble()
        applyBlur(border, shadow.blur)
        add(border)
        popContainer()
    }

    private fun applyBlur(graphics: Graphics, blur: Float) {
        if (blur <= 0f) return
        val filter = BlurFilter(json("strength" to blur.toDouble(), "quality" to 2))
        graphics.filters = arrayOf(filter)
        frameLeases.add(resources.register(WebRenderResourceKind.FILTER) { filter.destroy() })
    }

    private fun addStyledGraphics(graphics: Graphics, command: RenderCommand, fill: StyledFill?) {
        val mask = createStyledMask(command, fill)
        if (mask == null) {
            add(graphics)
            return
        }
        val container = Container()
        container.addChild(mask)
        container.addChild(graphics)
        container.mask = mask
        registerDisplayObject()
        registerDisplayObject()
        frameLeases.add(resources.register(WebRenderResourceKind.MASK))
        add(container)
    }

    private fun styledShape(command: RenderCommand, fill: StyledFill?): Graphics {
        val graphics = Graphics()
        if (fill == null || !fill.hasCornerRadius()) {
            return graphics.rect(command.x.toDouble(), command.y.toDouble(), command.w.toDouble(), command.h.toDouble())
        }
        return graphics.roundShape(
            arrayOf(
                roundedPoint(command.x, command.y, fill.cornerRadiusTL),
                roundedPoint(command.x + command.w, command.y, fill.cornerRadiusTR),
                roundedPoint(command.x + command.w, command.y + command.h, fill.cornerRadiusBR),
                roundedPoint(command.x, command.y + command.h, fill.cornerRadiusBL),
            ),
            0.0,
        )
    }

    private fun roundedPoint(x: Float, y: Float, radius: Float): dynamic =
        json("x" to x.toDouble(), "y" to y.toDouble(), "radius" to radius.coerceAtLeast(0f).toDouble())

    private fun strokeBorder(graphics: Graphics, fill: StyledFill?) {
        if (fill == null || !fill.hasBorder() || alpha(fill.borderColor) <= 0.0) return
        graphics.stroke(
            json(
                "width" to fill.borderWidth.toDouble(),
                "color" to rgb(fill.borderColor),
                "alpha" to alpha(fill.borderColor),
            ),
        )
    }

    private fun createGradient(fill: StyledFill): FillGradient {
        val options = if (fill.gradientType == GradientType.RADIAL) {
            json(
                "type" to "radial",
                "center" to point(fill.radialCenterX, fill.radialCenterY),
                "outerCenter" to point(fill.radialCenterX, fill.radialCenterY),
                "innerRadius" to 0.0,
                "outerRadius" to fill.radialRadius.coerceAtLeast(0f).toDouble(),
                "colorStops" to gradientStops(fill),
                "textureSpace" to "local",
            )
        } else {
            val dx = cos(fill.gradientAngleRadians) * 0.5f
            val dy = sin(fill.gradientAngleRadians) * 0.5f
            json(
                "type" to "linear",
                "start" to point(0.5f - dx, 0.5f - dy),
                "end" to point(0.5f + dx, 0.5f + dy),
                "colorStops" to gradientStops(fill),
                "textureSpace" to "local",
            )
        }
        return FillGradient(options)
    }

    private fun point(x: Float, y: Float): dynamic = json("x" to x.toDouble(), "y" to y.toDouble())

    private fun gradientStops(fill: StyledFill): Array<dynamic> = fill.gradientStops
        .map { stop -> json("offset" to stop.offset.toDouble(), "color" to cssColor(stop.argb)) }
        .toTypedArray()

    private fun cssColor(argb: Int): String =
        "rgba(${(argb ushr 16) and 0xFF},${(argb ushr 8) and 0xFF},${argb and 0xFF},${alpha(argb)})"

    private fun createStyledMask(command: RenderCommand, fill: StyledFill?): Graphics? {
        if (fill == null || !fill.hasMask()) return null
        val mask = Graphics()
        when (fill.maskType) {
            MaskType.NONE -> return null
            MaskType.RECT -> mask.rect(
                command.x.toDouble(),
                command.y.toDouble(),
                (command.w * fill.maskParam1.coerceIn(0f, 1f)).toDouble(),
                (command.h * fill.maskParam2.coerceIn(0f, 1f)).toDouble(),
            )
            MaskType.CIRCLE -> mask.circle(
                (command.x + command.w / 2f).toDouble(),
                (command.y + command.h / 2f).toDouble(),
                (min(command.w, command.h) * fill.maskParam1.coerceIn(0f, 1f) / 2f).toDouble(),
            )
            MaskType.PIE -> {
                val cx = command.x + command.w / 2f
                val cy = command.y + command.h / 2f
                val radius = min(command.w, command.h) / 2f
                mask.moveTo(cx.toDouble(), cy.toDouble())
                    .arc(cx.toDouble(), cy.toDouble(), radius.toDouble(), fill.maskParam1.toDouble(),
                        (fill.maskParam1 + fill.maskParam2).toDouble())
                    .lineTo(cx.toDouble(), cy.toDouble())
            }
            MaskType.WAVE -> mask.poly(waveMaskPoints(command, fill), true)
            MaskType.INVERT_RECT,
            MaskType.INVERT_CIRCLE,
            MaskType.INVERT_ROUND_RECT,
            -> createInverseMask(mask, command, fill)
        }
        return mask.fill(json("color" to 0xFFFFFF))
    }

    private fun createInverseMask(mask: Graphics, command: RenderCommand, fill: StyledFill) {
        mask.rect(command.x.toDouble(), command.y.toDouble(), command.w.toDouble(), command.h.toDouble())
        val holeW = command.w * fill.maskParam1.coerceIn(0f, 1f)
        val holeH = command.h * fill.maskParam2.coerceIn(0f, 1f)
        val holeX = command.x + (command.w - holeW) / 2f
        val holeY = command.y + (command.h - holeH) / 2f
        when (fill.maskType) {
            MaskType.INVERT_RECT -> mask.rect(holeX.toDouble(), holeY.toDouble(), holeW.toDouble(), holeH.toDouble())
            MaskType.INVERT_CIRCLE -> mask.circle(
                (command.x + command.w / 2f).toDouble(),
                (command.y + command.h / 2f).toDouble(),
                (min(command.w, command.h) * fill.maskParam1.coerceIn(0f, 1f) / 2f).toDouble(),
            )
            MaskType.INVERT_ROUND_RECT -> mask.roundRect(
                holeX.toDouble(), holeY.toDouble(), holeW.toDouble(), holeH.toDouble(),
                fill.maskParam3.coerceAtLeast(0f).toDouble(),
            )
            else -> Unit
        }
        mask.cut()
    }

    private fun waveMaskPoints(command: RenderCommand, fill: StyledFill): Array<Double> {
        val points = mutableListOf(command.x.toDouble(), (command.y + command.h).toDouble())
        val amplitude = fill.maskParam1
        val frequency = fill.maskParam2
        for (index in 0..WAVE_SEGMENTS) {
            val ratio = index.toFloat() / WAVE_SEGMENTS
            points.add((command.x + command.w * ratio).toDouble())
            points.add((command.y + command.h / 2f + sin(ratio * frequency + fill.maskParam3) * amplitude).toDouble())
        }
        points.add((command.x + command.w).toDouble())
        points.add((command.y + command.h).toDouble())
        return points.toTypedArray()
    }

    private fun submitRichText(command: RenderCommand) {
        drawRichText(
            command.text.orEmpty(), command.x, command.y, command.argb, command.scale,
            null, false, false, false, command.w, false, TEXT_HEIGHT,
        )
    }

    private fun submitItem(command: RenderCommand) {
        drawItemStack(
            command.itemId.orEmpty(), command.itemNbt, command.itemCount, false, true,
            "bottom-right", 0f, 0f, 0, command.x, command.y, command.w, command.h,
        )
    }

    private fun submitEntity(command: RenderCommand) {
        drawEntityPreview(
            command.entityRef.orEmpty(), null, null, command.x, command.y, command.w, command.h,
            command.mouseX, command.mouseY, 1f, 0f, 0f, false,
        )
    }

    private fun submitModel(command: RenderCommand) {
        createModelSlot(ModelCompositionRect(command.x, command.y, command.w, command.h), command.argb)
    }

    private fun createModelSlot(rect: ModelCompositionRect, backgroundColor: Int): Container {
        val slot = Container()
        slot.x = rect.x.toDouble()
        slot.y = rect.y.toDouble()
        pushContainer(slot)
        try {
            if (alpha(backgroundColor) > 0.0) fillRect(0f, 0f, rect.width, rect.height, backgroundColor)
        } finally {
            popContainer()
        }
        return slot
    }

    private fun drawModelPlaceholder(slot: Container, rect: ModelCompositionRect) {
        val previous = current
        current = slot
        try {
            drawPlaceholder(0f, 0f, rect.width, rect.height, MODEL_FILL, MODEL_BORDER, MODEL_LABEL)
        } finally {
            current = previous
        }
    }

    private fun modelCompositionRequest(
        trace: DrawTraceEntry?,
        state: StateSnapshot?,
        rect: ModelCompositionRect,
        instanceKey: String,
        source: String,
        texture: String?,
        modelBase: String,
        scale: Float,
        rotationX: Float,
        rotationY: Float,
        interactive: Boolean,
        backgroundColor: Int,
    ): ModelCompositionRequest = createModelCompositionRequest(
        sessionId = sessionId,
        generation = generation(),
        trace = trace,
        state = state,
        rect = rect,
        instanceKey = instanceKey,
        source = source,
        texture = texture,
        modelBase = modelBase,
        scale = scale,
        rotationX = rotationX,
        rotationY = rotationY,
        interactive = interactive,
        backgroundColor = backgroundColor,
    )

    private fun shouldComposeModel(request: ModelCompositionRequest): Boolean {
        if (request.alpha <= 0f || request.rect.width <= 0f || request.rect.height <= 0f) return false
        val clip = request.clip ?: return true
        if (clip.width <= 0f || clip.height <= 0f) return false
        return request.rect.x < clip.x + clip.width &&
            request.rect.x + request.rect.width > clip.x &&
            request.rect.y < clip.y + clip.height &&
            request.rect.y + request.rect.height > clip.y
    }

    private fun composeModel(request: ModelCompositionRequest, slot: Container, capturedFrame: Int) {
        val pending = runCatching { compositionHost.compose(request) }.getOrElse {
            showModelFailurePlaceholder(request, slot, capturedFrame, false)
            return
        }
        pending.then(
            onFulfilled = { result ->
                handleModelCompositionResult(result, request, slot, capturedFrame)
                Unit
            },
            onRejected = {
                showModelFailurePlaceholder(request, slot, capturedFrame, true)
                Unit
            },
        )
    }

    private fun handleModelCompositionResult(
        result: ModelCompositionResult,
        request: ModelCompositionRequest,
        slot: Container,
        capturedFrame: Int,
    ) {
        when (result) {
            is ModelCompositionResult.Ready -> acceptModelTexture(result.textureLease, request, slot, capturedFrame)
            is ModelCompositionResult.Placeholder -> showModelPlaceholder(request, slot, capturedFrame, true)
            ModelCompositionResult.Cancelled -> Unit
        }
    }

    private fun showModelFailurePlaceholder(
        request: ModelCompositionRequest,
        slot: Container,
        capturedFrame: Int,
        present: Boolean,
    ) {
        if (!showModelPlaceholder(request, slot, capturedFrame, present)) return
        emitModelCompositionFailure(request)
    }

    private fun showModelPlaceholder(
        request: ModelCompositionRequest,
        slot: Container,
        capturedFrame: Int,
        present: Boolean,
    ): Boolean {
        if (!acceptsModelResult(request, capturedFrame)) return false
        drawModelPlaceholder(slot, request.rect)
        emitModelPlaceholder(request)
        if (present) requestPresent()
        return true
    }

    private fun acceptModelTexture(
        lease: ModelTextureLease,
        request: ModelCompositionRequest,
        slot: Container,
        capturedFrame: Int,
    ) {
        if (!acceptsModelResult(request, capturedFrame)) {
            disposeLateModelTexture(lease, request)
            return
        }
        val resourceLease = resources.register(WebRenderResourceKind.OWNED_TEXTURE, lease::dispose)
        frameLeases.add(resourceLease)
        runCatching {
            lease.onTextureReady { texture ->
                runCatching {
                    if (acceptsModelResult(request, capturedFrame)) {
                        attachModelTexture(slot, request.rect, texture)
                        requestPresent()
                    } else {
                        resourceLease.release()
                    }
                }.onFailure {
                    resourceLease.release()
                    emitModelCompositionFailure(request)
                }
            }
        }.onFailure {
            resourceLease.release()
            emitModelCompositionFailure(request)
        }
    }

    private fun acceptsModelResult(request: ModelCompositionRequest, capturedFrame: Int): Boolean =
        !disposed && generation() == request.generation && frameGeneration == capturedFrame

    private fun attachModelTexture(slot: Container, rect: ModelCompositionRect, texture: Texture) {
        clearModelSlot(slot)
        val sprite = Sprite(json("texture" to texture))
        sprite.width = rect.width.toDouble()
        sprite.height = rect.height.toDouble()
        slot.addChild(sprite)
        registerDisplayObject()
    }

    private fun clearModelSlot(slot: Container) {
        slot.removeChildren().unsafeCast<Array<Container>>().forEach { child ->
            child.destroy(json("children" to true, "context" to true, "style" to true))
        }
    }

    private fun disposeLateModelTexture(lease: ModelTextureLease, request: ModelCompositionRequest) {
        if (runCatching(lease::dispose).isFailure) {
            emitModelTextureDisposeFailure(request)
        }
    }

    private fun emitModelPlaceholder(request: ModelCompositionRequest) {
        diagnostics.emit(
            WebRenderDiagnostic(
                "WEB_RENDER_PLACEHOLDER_CAPABILITY",
                WebRenderDiagnosticSeverity.INFO,
                "Web 模型预览合成不可用，已在原槽位绘制中文规范占位",
                sessionId,
                generation(),
                WebRenderCapabilityKeys.DRAW_MODEL_PREVIEW,
                canonicalWebResourceKey(request.source),
                request.phase,
                request.sequence,
            ),
        )
    }

    private fun emitModelCompositionFailure(request: ModelCompositionRequest) {
        diagnostics.emit(
            WebRenderDiagnostic(
                MODEL_COMPOSITION_FAILED_CODE,
                WebRenderDiagnosticSeverity.ERROR,
                "Web 模型合成请求失败，继续保留稳定占位",
                sessionId,
                generation(),
                WebRenderDiagnosticCapabilityKeys.MODEL_COMPOSITION,
                canonicalWebResourceKey(request.source),
                request.phase,
                request.sequence,
            ),
        )
    }

    private fun emitModelTextureDisposeFailure(request: ModelCompositionRequest) {
        diagnostics.emit(
            WebRenderDiagnostic(
                MODEL_TEXTURE_DISPOSE_FAILED_CODE,
                WebRenderDiagnosticSeverity.ERROR,
                "Web 模型纹理租约释放失败，已停止挂回场景",
                sessionId,
                generation(),
                WebRenderDiagnosticCapabilityKeys.MODEL_COMPOSITION,
                canonicalWebResourceKey(request.source),
                request.phase,
                request.sequence,
            ),
        )
    }

    private fun drawItemPlaceholder(
        itemId: String,
        count: Int,
        showCount: Boolean,
        countOffsetX: Float,
        countOffsetY: Float,
        backdropColor: Int,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        capabilityKey: String,
    ) {
        if (alpha(backdropColor) > 0.0) fillRect(x, y, width, height, backdropColor)
        drawPlaceholder(x, y, width, height, ITEM_FILL, ITEM_BORDER, shortName(itemId))
        if (showCount && count > 1) {
            drawItemCount(count, x, y, width, height, countOffsetX, countOffsetY)
        }
        emitPlaceholder(capabilityKey, itemId, "物品与槽位使用规范占位")
    }

    private fun drawItemCount(
        count: Int,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        offsetX: Float,
        offsetY: Float,
    ) {
        val label = count.toString()
        val countX = x + width - textWidth(label, COUNT_SCALE) - 1f + offsetX
        val countY = y + height - TEXT_HEIGHT * COUNT_SCALE - 1f + offsetY
        drawText(label, countX, countY, LABEL_COLOR, COUNT_SCALE)
    }

    private fun drawEntityPlaceholder(
        entityRef: String,
        entityType: String?,
        entityName: String?,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        capabilityKey: String,
    ) {
        if (entityRef.isEmpty()) return
        val label = shortName(entityName ?: entityType ?: entityRef)
        drawPlaceholder(x, y, width, height, ENTITY_FILL, ENTITY_BORDER, label)
        emitPlaceholder(capabilityKey, entityRef, "实体预览使用规范占位")
    }

    private fun pushContainer(container: Container) {
        add(container)
        containerStack.add(current)
        current = container
    }

    private fun popContainer() {
        if (containerStack.isNotEmpty()) {
            current = containerStack.removeAt(containerStack.lastIndex)
        }
    }

    private fun add(node: Container) {
        current.sortableChildren = true
        node.zIndex = currentZ.toDouble()
        current.addChild(node)
        registerDisplayObject()
        if (current === stage) {
            frameRoots.add(node)
        }
    }

    private fun registerDisplayObject() {
        frameLeases.add(resources.register(WebRenderResourceKind.DISPLAY_OBJECT))
    }

    private fun addSprite(
        sprite: Sprite,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        ownedTexture: Texture?,
        borrowedTexture: Boolean,
    ) {
        sprite.x = x.toDouble()
        sprite.y = y.toDouble()
        sprite.width = width.toDouble()
        sprite.height = height.toDouble()
        if (ownedTexture != null) {
            frameLeases.add(resources.register(WebRenderResourceKind.OWNED_TEXTURE) { ownedTexture.destroy(false) })
            frameLeases.add(resources.register(WebRenderResourceKind.BORROWED_BASE_TEXTURE))
        } else if (borrowedTexture) {
            frameLeases.add(resources.register(WebRenderResourceKind.BORROWED_TEXTURE))
            frameLeases.add(resources.register(WebRenderResourceKind.BORROWED_BASE_TEXTURE))
        }
        add(sprite)
    }

    private fun addText(
        text: String,
        x: Float,
        y: Float,
        argb: Int,
        scale: Float,
        fontName: String?,
        bold: Boolean,
        italic: Boolean,
    ) {
        val node = Text(json("text" to text, "style" to textStyle(argb, scale, fontName, bold, italic)))
        node.x = x.toDouble()
        node.y = y.toDouble()
        add(node)
    }

    private fun layoutRichText(
        text: String,
        scale: Float,
        fontName: String?,
        bold: Boolean,
        maxWidth: Float,
        multiline: Boolean,
        lineStep: Float,
    ): RichLayout {
        val lines = mutableListOf<RichLine>()
        var current = mutableListOf<RichToken>()
        var currentWidth = 0f
        fun finishLine() {
            lines.add(RichLine(current, currentWidth))
            current = mutableListOf()
            currentWidth = 0f
        }
        richTokens(text, scale, fontName, bold).forEach { token ->
            if (token.lineBreak) {
                if (multiline) finishLine()
                return@forEach
            }
            if (multiline && maxWidth > 0f && current.isNotEmpty() && currentWidth + token.width > maxWidth) {
                finishLine()
            }
            current.add(token)
            currentWidth += token.width
        }
        if (current.isNotEmpty() || lines.isEmpty()) finishLine()
        val hasInlineResource = lines.any { line ->
            line.tokens.any { token -> token.inlineLabel != null }
        }
        return RichLayout(lines, lines.maxOf { it.width }, lineStep, hasInlineResource)
    }

    private fun richTokens(text: String, scale: Float, fontName: String?, bold: Boolean): List<RichToken> {
        val tokens = mutableListOf<RichToken>()
        var index = 0
        while (index < text.length) {
            val tagEnd = if (text[index] == '[') text.indexOf(']', index + 1) else -1
            if (tagEnd > index && text.substring(index + 1, tagEnd).none { it == '[' || it == ']' }) {
                tokens.add(
                    RichToken(null, inlineLabel(text.substring(index, tagEnd + 1)), INLINE_SIZE * scale, false),
                )
                index = tagEnd + 1
            } else {
                addTextTokens(tokens, text[index].toString(), scale, fontName, bold)
                index++
            }
        }
        return tokens
    }

    private fun addTextTokens(
        target: MutableList<RichToken>,
        text: String,
        scale: Float,
        fontName: String?,
        bold: Boolean,
    ) {
        text.forEach { character ->
            if (character == '\n') {
                target.add(RichToken(null, null, 0f, true))
            } else {
                val value = character.toString()
                target.add(RichToken(value, null, textWidth(value, scale, fontName, bold), false))
            }
        }
    }

    private fun drawRichLine(
        line: RichLine,
        x: Float,
        y: Float,
        argb: Int,
        scale: Float,
        fontName: String?,
        shadow: Boolean,
        bold: Boolean,
        italic: Boolean,
    ) {
        var cursorX = x
        line.tokens.forEach { token ->
            if (token.text != null) {
                drawText(token.text, cursorX, y, argb, scale, fontName, shadow, bold, italic)
            } else if (token.inlineLabel != null) {
                drawPlaceholder(
                    cursorX,
                    y,
                    token.width,
                    INLINE_SIZE * scale,
                    INLINE_FILL,
                    INLINE_BORDER,
                    token.inlineLabel,
                )
            }
            cursorX += token.width
        }
    }

    private fun inlineLabel(tag: String): String = shortName(tag.removePrefix("[").removeSuffix("]"))

    private fun drawMissingResource(resource: String, x: Float, y: Float, width: Float, height: Float) {
        drawPlaceholder(x, y, width, height, IMAGE_FILL, IMAGE_BORDER, "缺失图片")
        val trace = currentTrace
        diagnostics.emit(
            WebRenderDiagnostic(
                "WEB_RENDER_RESOURCE_MISSING",
                WebRenderDiagnosticSeverity.WARNING,
                "Web 渲染资源不存在或无法解码，已使用规范占位",
                sessionId,
                generation(),
                WebRenderCapabilityKeys.DRAW_IMAGE,
                canonicalWebResourceKey(resource),
                trace?.phase,
                trace?.sequence ?: -1,
            ),
        )
    }

    private fun drawPlaceholder(x: Float, y: Float, w: Float, h: Float, fillArgb: Int, borderArgb: Int, label: String) {
        fillRect(x, y, w, h, fillArgb)
        add(
            Graphics()
                .rect(x.toDouble(), y.toDouble(), w.toDouble(), h.toDouble())
                .stroke(json("width" to 1.0, "color" to rgb(borderArgb), "alpha" to alpha(borderArgb))),
        )
        if (!(js("typeof document !== 'undefined'") as Boolean)) return
        val fitted = fitLabel(label, w)
        if (fitted.isNotEmpty()) {
            val textX = x + (w - textWidth(fitted, LABEL_SCALE)) / 2f
            drawText(fitted, textX, y + (h - TEXT_HEIGHT * LABEL_SCALE) / 2f, LABEL_COLOR, LABEL_SCALE)
        }
    }

    private fun capabilityKey(capability: UnsupportedRenderCapability): String = when (capability) {
        UnsupportedRenderCapability.COLOR_MATRIX -> WebRenderCapabilityKeys.PUSH_COLOR_MATRIX
        UnsupportedRenderCapability.FULL_TRANSFORM_3X3 -> WebRenderCapabilityKeys.PUSH_TRANSFORM_3X3
        UnsupportedRenderCapability.FULL_TRANSFORM_4X4 -> WebRenderCapabilityKeys.PUSH_TRANSFORM_4X4
        UnsupportedRenderCapability.FILTER_GROUP -> WebRenderCapabilityKeys.BEGIN_FILTER_GROUP
        UnsupportedRenderCapability.BACKDROP_FILTER -> WebRenderCapabilityKeys.APPLY_BACKDROP_BLUR
        UnsupportedRenderCapability.BLEND_MODE -> WebRenderCapabilityKeys.PUSH_BLEND_MODE
        UnsupportedRenderCapability.HISTORY_CAPTURE -> WebRenderCapabilityKeys.SUPPORTS_HISTORY_CAPTURE
    }

    private fun emitUnsupported(capabilityKey: String, trace: DrawTraceEntry? = currentTrace) {
        emitUnsupported(capabilityKey, trace?.phase, trace?.sequence ?: -1)
    }

    private fun emitUnsupported(
        capabilityKey: String,
        phase: top.wcpe.allincore.ui.runtime.render.layer.DrawTracePhase?,
        sequence: Int,
    ) {
        require(WebRenderCapabilityMatrix.containsKey(capabilityKey)) { "Web 渲染诊断能力键不在冻结矩阵中" }
        diagnostics.emit(
            WebRenderDiagnostic(
                "WEB_RENDER_UNSUPPORTED_CAPABILITY",
                WebRenderDiagnosticSeverity.WARNING,
                "Web 渲染器不支持该附加能力，基础内容继续回放",
                sessionId,
                generation(),
                capabilityKey,
                "",
                phase,
                sequence,
            ),
        )
    }

    private fun emitPlaceholder(capabilityKey: String, resourceKey: String, message: String) {
        require(WebRenderCapabilityMatrix.containsKey(capabilityKey)) { "Web 渲染诊断能力键不在冻结矩阵中" }
        val trace = currentTrace
        diagnostics.emit(
            WebRenderDiagnostic(
                "WEB_RENDER_PLACEHOLDER_CAPABILITY",
                WebRenderDiagnosticSeverity.INFO,
                message,
                sessionId,
                generation(),
                capabilityKey,
                canonicalWebResourceKey(resourceKey),
                trace?.phase,
                trace?.sequence ?: -1,
            ),
        )
    }

    private fun disposeOwnedCompositionHost() {
        if (!ownsCompositionHost || compositionHostDisposed) return
        compositionHostDisposed = true
        if (runCatching(compositionHost::dispose).isSuccess) return
        diagnostics.emit(
            WebRenderDiagnostic(
                MODEL_HOST_DISPOSE_FAILED_CODE,
                WebRenderDiagnosticSeverity.ERROR,
                "Web 模型合成宿主销毁失败，已停止继续使用",
                sessionId,
                generation(),
                WebRenderDiagnosticCapabilityKeys.MODEL_COMPOSITION,
                "",
                null,
                -1,
            ),
        )
    }

    private fun destroyFrameObjects(): Boolean {
        val failedRoots = frameRoots.filter { root ->
            runCatching { root.destroy(json("children" to true, "context" to true, "style" to true)) }.isFailure
        }
        if (failedRoots.isNotEmpty()) {
            frameRoots.clear()
            frameRoots.addAll(failedRoots)
            emitCleanupFailure()
            return false
        }
        frameRoots.clear()
        val failedLeases = frameLeases.filterNot(WebRenderResourceLease::release)
        frameLeases.clear()
        frameLeases.addAll(failedLeases)
        val disposed = failedLeases.isEmpty()
        if (!disposed) emitCleanupFailure()
        return disposed
    }

    private fun emitCleanupFailure() {
        diagnostics.emit(
            WebRenderDiagnostic(
                "WEB_RENDER_RESOURCE_DISPOSE_FAILED",
                WebRenderDiagnosticSeverity.ERROR,
                "Web 渲染帧资源释放失败，已继续清理其余资源",
                sessionId,
                generation(),
                WebRenderDiagnosticCapabilityKeys.RESOURCE_DISPOSE,
                "",
                null,
                -1,
            ),
        )
    }

    private fun fitLabel(label: String, maxWidth: Float): String {
        if (textWidth(label, LABEL_SCALE) <= maxWidth) return label
        var fitted = label
        while (fitted.isNotEmpty() && textWidth("$fitted…", LABEL_SCALE) > maxWidth) {
            fitted = fitted.dropLast(1)
        }
        return if (fitted.isEmpty()) "" else "$fitted…"
    }

    private fun textStyle(
        argb: Int,
        scale: Float,
        fontName: String?,
        bold: Boolean,
        italic: Boolean,
    ): TextStyle = TextStyle(
        json(
            "fontSize" to TEXT_HEIGHT * scale,
            "fontFamily" to (fontName ?: FONT_FAMILY),
            "fill" to rgb(argb),
            "fontWeight" to if (bold) "bold" else "normal",
            "fontStyle" to if (italic) "italic" else "normal",
        ),
    )

    private data class RichToken(val text: String?, val inlineLabel: String?, val width: Float, val lineBreak: Boolean)

    private data class RichLine(val tokens: List<RichToken>, val width: Float)

    private data class RichLayout(
        val lines: List<RichLine>,
        val width: Float,
        val lineStep: Float,
        val hasInlineResource: Boolean,
    )

    private fun shortName(id: String): String = id.substringAfterLast(':').substringAfterLast('/')
    private fun rgb(argb: Int): Int = argb and 0xFFFFFF
    private fun alpha(argb: Int): Double = ((argb ushr 24) and 0xFF) / 255.0
    private fun shadowColor(argb: Int): Int = (argb and 0xFF000000.toInt()) or ((rgb(argb) and 0xFCFCFC) shr 2)

    private companion object {
        const val FONT_FAMILY = "Consolas, 'Courier New', monospace"
        const val TEXT_HEIGHT = 9f
        const val INLINE_SIZE = 9f
        const val ITEM_FILL = 0x66FFB347
        const val ITEM_BORDER = 0xFFFFB347.toInt()
        const val ENTITY_FILL = 0x6687CEEB
        const val ENTITY_BORDER = 0xFF87CEEB.toInt()
        const val MODEL_FILL = 0x665D8AA8
        const val MODEL_BORDER = 0xFF5D8AA8.toInt()
        const val MODEL_LABEL = "模型预览"
        const val MODEL_COMPOSITION_FAILED_CODE = "WEB_RENDER_MODEL_COMPOSITION_FAILED"
        const val MODEL_TEXTURE_DISPOSE_FAILED_CODE = "WEB_RENDER_MODEL_TEXTURE_DISPOSE_FAILED"
        const val MODEL_HOST_DISPOSE_FAILED_CODE = "WEB_RENDER_MODEL_HOST_DISPOSE_FAILED"
        const val IMAGE_FILL = 0x66C08CFF
        const val IMAGE_BORDER = 0xFFC08CFF.toInt()
        const val INLINE_FILL = 0x667B68EE
        const val INLINE_BORDER = 0xFF7B68EE.toInt()
        const val LABEL_COLOR = 0xFFFFFFFF.toInt()
        const val LABEL_SCALE = 0.75f
        const val COUNT_SCALE = 0.8f
        const val WAVE_SEGMENTS = 24
        val ITEM_KEY_6 = WebRenderCapabilityKeys.DRAW_ITEM_STACK_6
        val ITEM_KEY_7 = WebRenderCapabilityKeys.DRAW_ITEM_STACK_7
        val ITEM_KEY_8 = WebRenderCapabilityKeys.DRAW_ITEM_STACK_8
        val ITEM_KEY_11 = WebRenderCapabilityKeys.DRAW_ITEM_STACK_12
        val ITEM_KEY_12 = WebRenderCapabilityKeys.DRAW_ITEM_STACK_13
        val ITEM_KEY_13 = WebRenderCapabilityKeys.DRAW_ITEM_STACK_14
        val ENTITY_KEY_7 = WebRenderCapabilityKeys.DRAW_ENTITY_PREVIEW_7
        val ENTITY_KEY_12 = WebRenderCapabilityKeys.DRAW_ENTITY_PREVIEW_12
        val ENTITY_KEY_13 = WebRenderCapabilityKeys.DRAW_ENTITY_PREVIEW_13
        val IMAGE_STYLE_KEY = WebRenderCapabilityKeys.DRAW_IMAGE_STYLED
        val COLOR_MATRIX_KEY = WebRenderCapabilityKeys.PUSH_COLOR_MATRIX
        val POP_COLOR_MATRIX_KEY = WebRenderCapabilityKeys.POP_COLOR_MATRIX
        val TRANSFORM_3_KEY = WebRenderCapabilityKeys.PUSH_TRANSFORM_3X3
        val POP_TRANSFORM_3_KEY = WebRenderCapabilityKeys.POP_TRANSFORM_3X3
        val TRANSFORM_4_KEY = WebRenderCapabilityKeys.PUSH_TRANSFORM_4X4
        val POP_TRANSFORM_4_KEY = WebRenderCapabilityKeys.POP_TRANSFORM_4X4
        val FILTER_KEY = WebRenderCapabilityKeys.BEGIN_FILTER_GROUP
        val END_FILTER_KEY = WebRenderCapabilityKeys.END_FILTER_GROUP
        val BACKDROP_KEY = WebRenderCapabilityKeys.APPLY_BACKDROP_BLUR
        val BLEND_KEY = WebRenderCapabilityKeys.PUSH_BLEND_MODE
        val POP_BLEND_KEY = WebRenderCapabilityKeys.POP_BLEND_MODE
        val NEON_GLOW_KEY = WebRenderCapabilityKeys.APPLY_NEON_GLOW
        val RIPPLE_KEY_9 = WebRenderCapabilityKeys.APPLY_RIPPLE_9
        val RIPPLE_KEY_10 = WebRenderCapabilityKeys.APPLY_RIPPLE_10
        val PROCEDURAL_TEXTURE_KEY = WebRenderCapabilityKeys.APPLY_PROCEDURAL_TEXTURE
        val FLOW_KEY = WebRenderCapabilityKeys.APPLY_FLOW
        val ENERGY_KEY = WebRenderCapabilityKeys.APPLY_ENERGY
    }
}

internal fun createModelCompositionRequest(
    sessionId: String,
    generation: Int,
    trace: DrawTraceEntry?,
    state: StateSnapshot?,
    rect: ModelCompositionRect,
    instanceKey: String,
    source: String,
    texture: String?,
    modelBase: String,
    scale: Float,
    rotationX: Float,
    rotationY: Float,
    interactive: Boolean,
    backgroundColor: Int,
): ModelCompositionRequest = ModelCompositionRequest(
    sessionId = sessionId,
    instanceKey = instanceKey,
    generation = generation,
    phase = trace?.phase ?: DrawTracePhase.CONTENT,
    sequence = trace?.sequence ?: -1,
    rect = rect,
    clip = state?.currentClip?.let { ModelCompositionRect(it[0], it[1], it[2], it[3]) },
    transform = modelTransform(state),
    alpha = state?.alphaStack?.fold(1f) { value, next -> value * next } ?: 1f,
    source = source,
    texture = texture,
    modelBase = modelBase,
    scale = scale,
    rotationX = rotationX,
    rotationY = rotationY,
    interactive = interactive,
    backgroundColor = backgroundColor,
)

private fun modelTransform(state: StateSnapshot?): FloatArray {
    val identity = floatArrayOf(1f, 0f, 0f, 1f, 0f, 0f)
    return state?.transformStack
        ?.asReversed()
        ?.fold(identity.copyOf()) { result, entry -> multiplyAffine(result, affine(entry)) }
        ?: identity
}

private fun affine(entry: TransformEntry): FloatArray = when (entry) {
    is TransformEntry.TranslateScale -> floatArrayOf(entry.sx, 0f, 0f, entry.sy, entry.tx, entry.ty)
    is TransformEntry.Matrix3x3 -> floatArrayOf(
        entry.mat[0], entry.mat[3], entry.mat[1], entry.mat[4], entry.mat[2], entry.mat[5],
    )
    is TransformEntry.Matrix4x4 -> floatArrayOf(
        entry.mat[0], entry.mat[1], entry.mat[4], entry.mat[5], entry.mat[12], entry.mat[13],
    )
}

private fun multiplyAffine(left: FloatArray, right: FloatArray): FloatArray = floatArrayOf(
    left[0] * right[0] + left[2] * right[1],
    left[1] * right[0] + left[3] * right[1],
    left[0] * right[2] + left[2] * right[3],
    left[1] * right[2] + left[3] * right[3],
    left[0] * right[4] + left[2] * right[5] + left[4],
    left[1] * right[4] + left[3] * right[5] + left[5],
)
